<template>
  <!--
    挂载方式与本文件的兄弟 ShareSettingDialog 完全一致（就挂在 .frame 根下）：
    ⚠️ :modal-append-to-body="false" 是必须的 —— 默认 true 时遮罩 .v-modal 挂到 document.body，
        而对话框本体留在 .frame（position:fixed 自成的层叠上下文，z-index 只有 auto），
        body 上 z-index 2001 的遮罩会反过来盖住对话框。
        关掉后遮罩被插到对话框旁边，两者同处一个层叠上下文，Element 给对话框的
        z-index 一定比遮罩大 → 层级正确；顺带还能继承 .frame 上的 --lp-* 工作表配色。
    垂直居中由 App.vue 的全局 `.el-dialog__wrapper{display:flex;align-items:center}` 负责。
  -->
  <el-dialog
    :visible="!!entry"
    :modal-append-to-body="false"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :show-close="true"
    width="400px"
    custom-class="running-timer-dialog"
    @close="onDismiss"
  >
    <div v-if="entry" class="rt">
      <!-- 头部：铃铛 + 到点提示 -->
      <div class="rt-head">
        <span class="rt-bell">⏰</span>
        <div class="rt-head-text">
          <span class="rt-title-main">时间到</span>
          <span class="rt-title-sub">你设定的 {{ entry.label }} 已经到了</span>
        </div>
      </div>

      <!-- 是哪一块 -->
      <div class="rt-block">
        <span v-if="entry.catLabel" class="rt-cat" :style="{ background: entry.catColor || '#cbd5e0' }" />
        <span class="rt-cat-name">{{ entry.catLabel || '无分类' }}</span>
        <span class="rt-text">{{ entry.title || '（无事项）' }}</span>
      </div>
      <div class="rt-elapsed">
        已经开始 <b>{{ elapsedLabel }}</b>
      </div>

      <!-- 再定时：快捷时长 + 自定义时刻，二选一 -->
      <div class="rt-again">
        <div class="rt-again-label">继续做的话，下一次什么时候提醒你？</div>
        <div class="rt-quick">
          <button
            v-for="m in QUICK"
            :key="m"
            type="button"
            class="rt-quick-btn"
            :class="{ on: quickActive === m }"
            @click="pickQuick(m)"
          >+{{ m }} 分钟</button>
        </div>
        <div class="rt-custom">
          <span class="rt-custom-label">或指定时刻</span>
          <el-time-picker
            v-model="customTime"
            popper-class="quick-add-time-picker"
            size="small"
            placeholder="选择时刻"
            format="HH:mm"
            value-format="HH:mm"
            :clearable="false"
            @change="onCustomChange"
          />
        </div>
      </div>

      <p v-if="!granted" class="rt-hint">
        系统通知未开启，提醒只会显示在当前页面，请勿长时间切到其他页面。
      </p>
    </div>

    <template #footer>
      <div class="rt-footer">
        <el-button size="small" @click="onDismiss">稍后说</el-button>
        <el-button size="small" type="primary" @click="onContinue">继续（再定时）</el-button>
        <el-button size="small" type="danger" plain @click="onEnd">结束</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { notifyGranted } from '@/utils/runningTimer'
import { minutesToLabel } from './constants'
import { portalToFullscreen } from '@/utils/fullscreenPortal'

const QUICK = [5, 10, 25, 45]

export default {
  name: 'RunningTimerDialog',
  props: {
    /** 待处理的定时条目（timerState.queue[0]），为 null 时不显示 */
    entry: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      QUICK,
      quickActive: 25,
      customTime: '',
      granted: notifyGranted(),
      // 已开始的分钟数，每秒刷新一次，让用户知道这块做了多久
      elapsed: 0,
      ticker: null
    }
  },
  computed: {
    elapsedLabel() {
      const m = Math.max(0, this.elapsed)
      if (m < 60) return `${m} 分钟`
      return `${Math.floor(m / 60)} 小时 ${m % 60} 分钟`
    }
  },
  watch: {
    entry: {
      immediate: true,
      handler(val) {
        this.stopTick()
        if (!val) return
        // 每次处理一条新的提醒，回到「+25 分钟」这个默认值
        this.quickActive = 25
        this.customTime = ''
        this.elapsed = this.calcElapsed(val)
        this.ticker = setInterval(() => {
          this.elapsed = this.calcElapsed(val)
        }, 1000)
        // 全屏视图：对话框挂在 TaskBlockPage 的 .frame 下，会被全屏中的 .tablebox
        // （浏览器 top layer）盖住 —— 搬进全屏元素（非全屏时 no-op）。
        // 放在 $nextTick 里：el-dialog 自己的 visible→open 也在 nextTick 链上，
        // 搬完之后它插入的 .v-modal 会作为兄弟节点一起进全屏元素。
        this.$nextTick(() => portalToFullscreen(this.$el))
      }
    }
  },
  beforeDestroy() {
    this.stopTick()
  },
  methods: {
    stopTick() {
      if (this.ticker) {
        clearInterval(this.ticker)
        this.ticker = null
      }
    },
    calcElapsed(entry) {
      if (!entry || !entry.startedAt) return 0
      return Math.max(0, Math.round((Date.now() - entry.startedAt) / 60000))
    },
    pickQuick(m) {
      this.quickActive = m
      this.customTime = ''
    },
    onCustomChange() {
      this.quickActive = null
    },
    /** 把「相对分钟 / 指定时刻」换算成下一次触发的绝对时刻 */
    resolveNextAt() {
      const now = Date.now()
      if (this.customTime) {
        const [h, m] = this.customTime.split(':').map(Number)
        const d = new Date(now)
        d.setHours(h, m, 0, 0)
        // 选了一个已经过去的时刻（比如跨了午夜）→ 顺延到明天
        if (d.getTime() <= now) d.setDate(d.getDate() + 1)
        return { at: d.getTime(), label: this.customTime }
      }
      const m = this.quickActive || 25
      const d = new Date(now + m * 60000)
      return { at: d.getTime(), label: minutesToLabel(d.getHours() * 60 + d.getMinutes()) }
    },
    onContinue() {
      if (!this.entry) return
      const next = this.resolveNextAt()
      this.$emit('continue', { ...next })
    },
    onEnd() {
      this.$emit('end')
    },
    onDismiss() {
      this.$emit('dismiss')
    }
  }
}
</script>

<style scoped>
/* 提醒弹窗：白底卡片 + 一圈暖色描边，和「在做」的蓝色调区分开，
   让人一眼看出这不是普通的确认框 */
.rt{
  display:flex;
  flex-direction:column;
  gap:0.75rem;
}
.rt-head{
  display:flex;
  align-items:center;
  gap:0.625rem;
}
.rt-bell{
  font-size:1.5rem;
  line-height:1;
}
.rt-head-text{
  display:flex;
  flex-direction:column;
  gap:0.125rem;
}
.rt-title-main{
  font-size:1.0625rem;
  font-weight:600;
  color:#2d3748;
}
.rt-title-sub{
  font-size:0.75rem;
  color:#718096;
}
.rt-block{
  display:flex;
  align-items:center;
  gap:0.375rem;
  padding:0.5rem 0.625rem;
  background:#f7fafc;
  border-radius:0.5rem;
  border-left:3px solid var(--lp-primary,#558b2f);
  min-width:0;
}
.rt-cat{
  width:0.5rem;
  height:0.5rem;
  border-radius:3px;
  flex:0 0 auto;
}
.rt-cat-name{
  font-size:0.75rem;
  font-weight:600;
  color:#2d3748;
  flex:0 0 auto;
}
.rt-text{
  font-size:0.75rem;
  color:#4a5568;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.rt-elapsed{
  font-size:0.75rem;
  color:#718096;
}
.rt-elapsed b{
  color:#2d3748;
}
.rt-again-label{
  font-size:0.75rem;
  color:#4a5568;
  margin-bottom:0.375rem;
}
.rt-quick{
  display:flex;
  flex-wrap:wrap;
  gap:0.375rem;
}
.rt-quick-btn{
  padding:0.25rem 0.625rem;
  font-family:inherit;
  font-size:0.75rem;
  color:#4a5568;
  background:#fff;
  border:1px solid #e2e8f0;
  border-radius:0.75rem;
  cursor:pointer;
  transition:background .15s, color .15s, border-color .15s;
}
.rt-quick-btn:hover{
  border-color:var(--lp-primary,#558b2f);
  color:var(--lp-primary-dark,#2f7a4a);
}
.rt-quick-btn.on{
  background:var(--lp-primary,#558b2f);
  border-color:var(--lp-primary,#558b2f);
  color:#fff;
  font-weight:600;
}
.rt-custom{
  display:flex;
  align-items:center;
  gap:0.5rem;
  margin-top:0.5rem;
}
.rt-custom-label{
  font-size:0.75rem;
  color:#a0aec0;
  flex:0 0 auto;
}
.rt-hint{
  margin:0;
  font-size:0.6875rem;
  color:#b7791f;
  line-height:1.5;
}
.rt-footer{
  display:flex;
  justify-content:flex-end;
  gap:0.5rem;
}
</style>
